import type { RevenueFigure, RevenueSummary, SummaryMetricKey } from '@/api/revenue'
import { formatInteger, formatPercent, formatValue, KpiCard, LoadingBlock, UnavailableKpiCard, type FormatOptions, type Polarity, type ValueFormat } from '@/design'

import { Definition } from '../Definition'
import { comparisonLabel, METRIC_DEFINITIONS, NOT_AVAILABLE_TEXT, NULL_REASON_TEXT, PREVIOUS_UNAVAILABLE_TEXT, REVENUE_LABEL } from '../format'

interface TileSpec {
  key: SummaryMetricKey
  label: string
  format: ValueFormat
  polarity: Polarity
  /** The figure's sub-line, from the summary's other figures. */
  detail?: (summary: RevenueSummary, money: FormatOptions) => string | undefined
}

/** The row, in the proposal's order; both revenues whatever the basis (decision 1). */
const TILES: readonly TileSpec[] = [
  { key: 'revenue_gross', label: REVENUE_LABEL.gross, format: 'currency', polarity: 'up_good' },
  { key: 'revenue_net', label: REVENUE_LABEL.net, format: 'currency', polarity: 'up_good' },
  {
    key: 'orders',
    label: 'Orders',
    format: 'integer',
    polarity: 'up_good',
    detail: ({ metrics }, money) => zeroValueNote(metrics.orders_zero_value.value, money),
  },
  { key: 'aov', label: 'AOV', format: 'currency', polarity: 'up_good' },
  { key: 'tickets_sold', label: 'Tickets sold', format: 'integer', polarity: 'up_good' },
  { key: 'average_ticket_price', label: 'Avg ticket price', format: 'currency', polarity: 'neutral' },
  {
    key: 'refunds',
    label: 'Refunds',
    format: 'currency',
    polarity: 'down_good',
    detail: ({ metrics }) => {
      const rate = metrics.refund_rate
      return rate.value === null ? `Refund rate — · ${NULL_REASON_TEXT[rate.nullReason ?? 'no_revenue']}` : `${formatPercent(rate.value)} of gross`
    },
  },
  {
    key: 'discounts',
    label: 'Discounts',
    format: 'currency',
    polarity: 'neutral',
    detail: ({ metrics }, money) => {
      const net = metrics.revenue_net_before_refunds.value
      return net === null ? undefined : `Net before refunds ${formatValue(net, 'currency', money)}`
    },
  },
  { key: 'revenue_per_visitor', label: 'Revenue / visitor', format: 'currency', polarity: 'up_good' },
]

function zeroValueNote(zeroValue: number | null, money: FormatOptions): string | undefined {
  if (zeroValue === null) return undefined
  if (zeroValue === 0) return 'No zero-value orders'
  return `${formatInteger(zeroValue, money)} zero-value ${zeroValue === 1 ? 'order' : 'orders'} not counted`
}

/** What the tile says in place of a change: why the figure, or its comparison, has none. */
function noChangeNote(figure: RevenueFigure, summary: RevenueSummary): string | undefined {
  if (figure.value === null) return figure.nullReason ? NULL_REASON_TEXT[figure.nullReason] : undefined
  if (summary.previousUnavailable) return PREVIOUS_UNAVAILABLE_TEXT[summary.previousUnavailable]
  if (figure.previous === null) return figure.previousNullReason ? `Previous: ${NULL_REASON_TEXT[figure.previousNullReason]}` : undefined
  if (figure.delta && figure.delta.ratio === null) return 'None in the comparison period'
  return undefined
}

function Tile({ spec, summary, money }: { spec: TileSpec; summary: RevenueSummary; money: FormatOptions }) {
  const figure = summary.metrics[spec.key]
  const definition = <Definition label={spec.label} text={METRIC_DEFINITIONS[spec.key]} />
  const notAvailable = summary.notAvailable[spec.key]
  if (notAvailable) {
    return <UnavailableKpiCard label={spec.label} value="Not available" note={NOT_AVAILABLE_TEXT[notAvailable]} badge={definition} />
  }
  return (
    <KpiCard
      label={spec.label}
      value={figure.value === null ? '—' : formatValue(figure.value, spec.format, money)}
      change={figure.value === null ? null : (figure.delta?.ratio ?? null)}
      polarity={spec.polarity}
      comparisonLabel={comparisonLabel(summary)}
      note={noChangeNote(figure, summary)}
      detail={spec.detail?.(summary, money)}
      badge={definition}
    />
  )
}

/**
 * The summary's headline figures under the page's basis, each beside its comparison:
 * the Overview's tile presentation (`KpiCard`, and its not-available tile), so a figure
 * reads the same on both pages. A null figure is "—" with its reason, a figure this read
 * model does not have is "Not available" with what it needs: never 0.
 */
export function RevenueKpis({ summary, money, busy }: { summary: RevenueSummary | undefined; money: FormatOptions; busy: boolean }) {
  return (
    <section aria-labelledby="rv-kpis-heading">
      <h2 id="rv-kpis-heading" className="ci-visually-hidden">
        Headline figures
      </h2>
      <ul className="rv-kpis" aria-busy={busy || undefined}>
        {TILES.map((spec) => (
          <li key={spec.key} data-kpi={spec.key}>
            {summary ? (
              <Tile spec={spec} summary={summary} money={money} />
            ) : (
              <div className="ci-kpi">
                <LoadingBlock rows={2} />
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
